import React, { useState, useEffect } from 'react';
import AlumniDetailModal from './AlumniDetailModal';
import AddToCampaignModal from './AddToCampaignModal';
import { searchLinkedInWithOpenAI } from '../../utils/apiService';
import { advancedSearch, highlightMatches } from '../../utils/searchUtils';
import { formatName } from '../../utils/nameUtils';

// Format date to remove time component
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // If the date includes time (0:00), remove it
  if (dateString.includes('0:00')) {
    return dateString.split(' ')[0];
  }
  
  return dateString;
};

const AlumniTable = ({ alumni: initialAlumni, searchTerm, sortConfig, requestSort }) => {
  // Use state for alumni data but always initialize from props
  // This allows us to make local updates (like LinkedIn search results)
  // while still respecting the filtered data from the parent component
  const [alumni, setAlumni] = useState(initialAlumni);
  
  // Always update local state when props change to respect filters
  useEffect(() => {
    setAlumni(initialAlumni);
  }, [initialAlumni]);
  
  // State for modals and UI
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchingIds, setSearchingIds] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedAlumnus, setSelectedAlumnus] = useState(null);
  
  // Listen for data update events
  useEffect(() => {
    const handleDataUpdated = () => {
      console.log('Alumni data updated event received');
      // The parent component will handle data updates and filtering
    };
    
    // Add event listener for data updates
    window.addEventListener('alumni_data_updated', handleDataUpdated);
    
    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('alumni_data_updated', handleDataUpdated);
    };
  }, []);
  
  // Filter alumni based on search term using advanced search
  const filteredAlumni = searchTerm.trim() ? advancedSearch(alumni, searchTerm) : alumni;

  // Sort alumni based on sort config
  const sortedAlumni = [...filteredAlumni].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue < bValue) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  // Function to get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Function to handle LinkedIn search with improved error handling and timeout
  const handleLinkedInSearch = async (alumnus) => {
    // Safety check for alumnus
    if (!alumnus || !alumnus.emp_empcode) {
      console.error('Invalid alumnus data provided to LinkedIn search');
      return;
    }
    
    // If already searching for this alumnus, don't start another search
    if (searchingIds.includes(alumnus.emp_empcode)) {
      console.log(`Already searching for ${alumnus.emp_empname}, ignoring duplicate request`);
      return;
    }
    
    // Add this alumni's ID to the searching array and track start time
    setSearchingIds(prev => [...prev, alumnus.emp_empcode]);
    setSearchError(null);
    
    // Track when the search started
    alumnus.searchStartTime = new Date().getTime();
    console.log(`Starting LinkedIn search for ${alumnus.emp_empname} at ${new Date().toISOString()}`);
    
    // Set a timeout to cancel the search if it takes too long (45 seconds)
    const searchTimeout = setTimeout(() => {
      console.log(`Search timeout for ${alumnus.emp_empname}`);
      // If still searching, cancel it
      if (searchingIds.includes(alumnus.emp_empcode)) {
        setSearchingIds(prev => prev.filter(id => id !== alumnus.emp_empcode));
        setSearchError(`Search for ${alumnus.emp_empname} timed out. Please try again.`);
        
        // Update the status of the alumnus
        setAlumni(prevAlumni => {
          return prevAlumni.map(item => {
            if (item.emp_empcode === alumnus.emp_empcode) {
              return { ...item, actionStatus: 'Search timed out' };
            }
            return item;
          });
        });
      }
    }, 45000); // 45 second timeout
    
    try {
      console.log(`Searching LinkedIn for ${alumnus.emp_empname} using backend API`);
      
      // Show searching status immediately
      setAlumni(prevAlumni => {
        return prevAlumni.map(item => {
          if (item.emp_empcode === alumnus.emp_empcode) {
            return { ...item, actionStatus: 'Searching...' };
          }
          return item;
        });
      });
      
      console.log(`Starting LinkedIn API call for ${alumnus.emp_empname}`);
      
      // Call the backend API through our utility function with a timeout
      const linkedInData = await Promise.race([
        searchLinkedInWithOpenAI(alumnus),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('API request timed out')), 40000)
        )
      ]);
      
      // Clear the timeout since we got a response
      clearTimeout(searchTimeout);
      console.log(`LinkedIn API call completed for ${alumnus.emp_empname}, clearing timeout`);
      
      // Force clear the searching state for this alumnus
      console.log(`Removing ${alumnus.emp_empcode} from searchingIds after successful API call`);
      setSearchingIds(prev => {
        const newIds = prev.filter(id => id !== alumnus.emp_empcode);
        console.log(`New searchingIds after removal:`, newIds);
        return newIds;
      });
      
      // Safety check - verify the component is still mounted
      // Note: We've already cleared the searching state, so we just check if the component is mounted
      if (!document.body.contains(document.getElementById('alumni-table-container'))) {
        console.log('Component unmounted, aborting update');
        return;
      }
      
      console.log('LinkedIn data found:', linkedInData);
      
      // Safely update the alumni data
      setAlumni(prevAlumni => {
        return prevAlumni.map(item => {
          if (item.emp_empcode === alumnus.emp_empcode) {
            const updatedItem = { ...item };
            
            try {
              if (linkedInData && linkedInData.found) {
                // Update with LinkedIn data
                updatedItem.currentTitle = linkedInData.currentPosition?.title || 'Not available';
                updatedItem.currentCompany = linkedInData.currentPosition?.company || 'Not available';
                updatedItem.linkedInUrl = linkedInData.profileUrl || '';
                updatedItem.industry = linkedInData.industry || 'Not available';
                updatedItem.location = linkedInData.location || 'Not available';
                updatedItem.leadershipScore = linkedInData.leadershipScore || 0;
                updatedItem.skills = linkedInData.skills || [];
                updatedItem.education = linkedInData.education || [];
                updatedItem.previousPositions = linkedInData.previousPositions || [];
                updatedItem.confidence = linkedInData.confidence || 0;
                updatedItem.reasoning = linkedInData.reasoning || '';
                updatedItem.lastUpdated = new Date().toISOString();
                updatedItem.actionStatus = 'LinkedIn profile found!';
              } else {
                // No data found
                updatedItem.currentTitle = 'Not found';
                updatedItem.currentCompany = 'Not found';
                updatedItem.reasoning = (linkedInData && linkedInData.reasoning) || 'Could not find information';
                updatedItem.lastUpdated = new Date().toISOString();
                updatedItem.actionStatus = 'No profile found';
              }
            } catch (dataError) {
              console.error('Error processing LinkedIn data:', dataError);
              updatedItem.actionStatus = 'Error processing data';
            }
            
            return updatedItem;
          }
          return item;
        });
      });
      
      // Remove this alumni's ID from the searching array
      setSearchingIds(prev => prev.filter(id => id !== alumnus.emp_empcode));
      
      // Update the modal if it's open for this alumnus
      try {
        if (showModal && selectedAlumni && selectedAlumni.emp_empcode === alumnus.emp_empcode) {
          // Get the updated alumnus from the current state
          setAlumni(currentAlumni => {
            const updatedAlumnus = currentAlumni.find(a => a.emp_empcode === alumnus.emp_empcode);
            if (updatedAlumnus) {
              setSelectedAlumni(updatedAlumnus);
            }
            return currentAlumni; // Return unchanged state
          });
        }
      } catch (modalError) {
        console.error('Error updating modal:', modalError);
      }
      
      // Clear the action status after a delay
      const statusTimeout = setTimeout(() => {
        setAlumni(prevAlumni => {
          return prevAlumni.map(item => {
            if (item.emp_empcode === alumnus.emp_empcode) {
              return { ...item, actionStatus: '' };
            }
            return item;
          });
        });
      }, 3000);
      
      // Clean up timeout if component unmounts
      return () => clearTimeout(statusTimeout);
      
    } catch (error) {
      // Clear the search timeout since we got an error
      clearTimeout(searchTimeout);
      
      console.error('Error searching LinkedIn:', error);
      
      // Handle API errors specifically
      if (error.message === 'API request timed out') {
        setSearchError(`Search for ${alumnus.emp_empname} timed out. Please try again.`);
      } else if (error.message && error.message.includes('API Error: OpenAI API key is not set')) {
        setSearchError('OpenAI API key is not configured. Please contact the administrator.');
      } else {
        setSearchError(error.message || 'An error occurred during the search');
      }
      
      // Update the status of the alumnus
      setAlumni(prevAlumni => {
        return prevAlumni.map(item => {
          if (item.emp_empcode === alumnus.emp_empcode) {
            return { ...item, actionStatus: 'Search failed' };
          }
          return item;
        });
      });
      
      // Clear the error status after a delay
      const errorTimeout = setTimeout(() => {
        setAlumni(prevAlumni => {
          return prevAlumni.map(item => {
            if (item.emp_empcode === alumnus.emp_empcode) {
              return { ...item, actionStatus: '' };
            }
            return item;
          });
        });
      }, 3000);
      
      // Remove this alumni's ID from the searching array
      setSearchingIds(prev => prev.filter(id => id !== alumnus.emp_empcode));
      
      // Clean up timeout if component unmounts
      return () => clearTimeout(errorTimeout);
    }
  };


  // Function to open alumni detail modal
  const openAlumniDetail = (alumnus) => {
    setSelectedAlumni(alumnus);
    setShowModal(true);
  };

  return (
    <div id="alumni-table-container" className="table-container overflow-x-auto rounded-lg shadow-lg border border-gray-200">
      <table className="data-table w-full table-fixed">
        <thead>
          <tr className="bg-gradient-to-r from-primary to-blue-700 text-white">
            <th 
              onClick={() => requestSort('emp_empname')}
              className="w-1/5 px-6 py-4 text-center font-semibold cursor-pointer hover:bg-blue-600 transition-colors duration-200 rounded-tl-lg"
            >
              <div className="flex items-center justify-center">
                <span>Name</span>
                <span className="ml-1">{getSortDirectionIndicator('emp_empname')}</span>
              </div>
            </th>
            <th 
              onClick={() => requestSort('currentTitle')}
              className="w-1/5 px-6 py-4 text-center font-semibold cursor-pointer hover:bg-blue-600 transition-colors duration-200"
            >
              <div className="flex items-center justify-center">
                <span>Current Title</span>
                <span className="ml-1">{getSortDirectionIndicator('currentTitle')}</span>
              </div>
            </th>
            <th 
              onClick={() => requestSort('currentCompany')}
              className="w-1/5 px-6 py-4 text-center font-semibold cursor-pointer hover:bg-blue-600 transition-colors duration-200"
            >
              <div className="flex items-center justify-center">
                <span>Current Company</span>
                <span className="ml-1">{getSortDirectionIndicator('currentCompany')}</span>
              </div>
            </th>
            <th 
              onClick={() => requestSort('emp_startdate')}
              className="w-1/5 px-6 py-4 text-center font-semibold cursor-pointer hover:bg-blue-600 transition-colors duration-200"
            >
              <div className="flex items-center justify-center">
                <span>Date Joined Dynpro</span>
                <span className="ml-1">{getSortDirectionIndicator('emp_startdate')}</span>
              </div>
            </th>
            <th className="w-1/5 px-6 py-4 text-center font-semibold rounded-tr-lg">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedAlumni.map((alumnus, index) => (
            <tr 
              key={index} 
              className={`${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'} hover:bg-blue-100 transition-colors duration-200`}
            >
              <td 
                className="px-6 py-4 font-medium text-primary cursor-pointer hover:text-blue-700 hover:underline flex items-center"
                onClick={() => openAlumniDetail(alumnus)}
              >
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center mr-3 font-bold">
                  {formatName(alumnus.emp_empname).charAt(0)}
                </div>
                <span>{formatName(alumnus.emp_empname)}</span>
              </td>
              <td className="px-6 py-4">
                {alumnus.currentTitle ? (
                  <span className="font-medium">{alumnus.currentTitle}</span>
                ) : (
                  <span className="text-gray-400 italic">Not available</span>
                )}
              </td>
              <td className="px-6 py-4">
                {alumnus.currentCompany ? (
                  <span className="font-medium">{alumnus.currentCompany}</span>
                ) : (
                  <span className="text-gray-400 italic">Not available</span>
                )}
              </td>
              <td className="px-6 py-4 text-center">
                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg font-medium">
                  {formatDate(alumnus.emp_startdate)}
                </span>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  {/* Fixed action labels */}
                  <div className="relative w-full">
                    <div className="search-label absolute top-0 left-0 right-0 text-center text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                      Search LinkedIn
                    </div>
                    <div className="searching-label absolute top-0 left-0 right-0 text-center text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                      Searching LinkedIn...
                    </div>
                    <div className="found-label absolute top-0 left-0 right-0 text-center text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                      LinkedIn profile found!
                    </div>
                    <div className="not-found-label absolute top-0 left-0 right-0 text-center text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                      No profile found
                    </div>
                    <div className="view-label absolute top-0 left-0 right-0 text-center text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                      View LinkedIn Profile
                    </div>
                    <div className="opening-label absolute top-0 left-0 right-0 text-center text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                      Opening LinkedIn Profile...
                    </div>
                  </div>
                  
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      if (!searchingIds.includes(alumnus.emp_empcode)) {
                        handleLinkedInSearch(alumnus);
                      } else {
                        // Force clear the searching state if it's been stuck for more than 1 minute
                        const currentTime = new Date().getTime();
                        const searchStartTime = alumnus.searchStartTime || 0;
                        if (currentTime - searchStartTime > 60000) { // 1 minute timeout
                          console.log(`Forcing search reset for ${alumnus.emp_empname} after timeout`);
                          setSearchingIds(prev => prev.filter(id => id !== alumnus.emp_empcode));
                        }
                      }
                    }}
                    className={`${searchingIds.includes(alumnus.emp_empcode) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white text-xs px-2 py-1 rounded shadow-sm transition-colors duration-200 flex items-center`}
                    disabled={searchingIds.includes(alumnus.emp_empcode)}
                  >
                    {searchingIds.includes(alumnus.emp_empcode) ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                        </svg>
                        Search LinkedIn
                      </>
                    )}
                  </button>
                  {alumnus.linkedInUrl && (
                    <a 
                      href={alumnus.linkedInUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-48 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors duration-200"
                      onMouseEnter={(e) => {
                        const label = e.currentTarget.parentNode.querySelector('.view-label');
                        if (label) label.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        const label = e.currentTarget.parentNode.querySelector('.view-label');
                        if (label) label.style.opacity = '0';
                      }}
                      onClick={(e) => {
                        const viewLabel = e.currentTarget.parentNode.querySelector('.view-label');
                        const openingLabel = e.currentTarget.parentNode.querySelector('.opening-label');
                        if (viewLabel) viewLabel.style.opacity = '0';
                        if (openingLabel) openingLabel.style.opacity = '1';
                        
                        setTimeout(() => {
                          if (openingLabel) openingLabel.style.opacity = '0';
                        }, 2000);
                      }}
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      View Profile
                    </a>
                  )}
                  
                  {/* Add to Campaign Button */}
                  <button
                    className="w-48 inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors duration-200"
                    onClick={() => {
                      setSelectedAlumnus(alumnus);
                      setShowCampaignModal(true);
                    }}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add to Campaign
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedAlumni.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p className="text-lg font-medium">No results found</p>
          <p className="text-gray-400">No alumni matching "{searchTerm}" were found</p>
        </div>
      )}
      
      {/* Alumni Detail Modal */}
      {showModal && selectedAlumni && (
        <AlumniDetailModal 
          alumni={selectedAlumni} 
          onClose={() => setShowModal(false)} 
        />
      )}
      
      {/* Add to Campaign Modal */}
      {showCampaignModal && selectedAlumnus && (
        <AddToCampaignModal
          onClose={() => setShowCampaignModal(false)}
          onSave={(campaign, alumnus) => {
            // The campaign object from the API already contains the updated contact count
            // and target count, so we don't need to manage it manually
            alert(`${alumnus.emp_empname} has been added to the "${campaign.name}" campaign.`);
            setShowCampaignModal(false);
          }}
          alumnus={selectedAlumnus}
        />
      )}
      
      {/* Error Message */}
      {searchError && (
        <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
          <div className="flex">
            <div className="py-1">
              <svg className="w-6 h-6 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <p className="font-bold">Search Error</p>
              <p className="text-sm">{searchError}</p>
              <button 
                className="text-sm underline mt-1"
                onClick={() => setSearchError(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlumniTable;
